/**
 * Last-modified rollup — deliberately kept in its own module with ZERO
 * `@/lib/i18n` dependency. `dateUtils.ts` imports i18n (for locale-aware
 * formatting), so importing this rollup from `recapService` via `dateUtils`
 * would drag i18n's `initReactI18next` into recapService's module graph and
 * break every test that partial-mocks `react-i18next` (STK-750 CI fix).
 */

/** Absolute-instant ms for an API datetime (UTC+0, "Z" optional per the
 * datetime convention). Lets us compare timestamps regardless of the "Z"
 * suffix inconsistency between offline-mapped and REST rows. Only appends the
 * "Z" designator when no timezone is already present — otherwise an
 * offset-bearing value like "2026-06-16T11:00:00+00:00" would become
 * "…+00:00Z" → `new Date(...)` = NaN, corrupting the rollup pick/sort. Mirrors
 * the canonical `parseAsUtc` helper. */
export function apiInstantMs(value: string): number {
  const hasTz = value.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(value);
  return new Date(hasTz ? value : value + "Z").getTime();
}

/**
 * Last-modified rollup for a pre-order: the most recent of the header's own
 * `updated_at` and every flow's `updated_at`. A line/flow edit bumps the flow
 * row, not the parent header, so the header timestamp alone would understate
 * when the order last changed — this rolls both up (STK-750). Falls back to
 * `created_at` when no `updated_at` is present. Returns the winning ISO string.
 */
export function getLastModifiedAt(po: {
  updated_at?: string | null;
  created_at: string;
  flows?: ReadonlyArray<{ updated_at?: string | null }> | null;
}): string {
  let best = po.updated_at ?? po.created_at;
  let bestMs = apiInstantMs(best);
  for (const flow of po.flows ?? []) {
    const u = flow.updated_at;
    if (!u) continue;
    const ms = apiInstantMs(u);
    if (ms > bestMs) {
      best = u;
      bestMs = ms;
    }
  }
  return best;
}

/**
 * Last-modified rollup as an absolute-instant timestamp (ms). Use this for
 * sorting — comparing the raw ISO strings with `localeCompare` breaks on mixed
 * `Z`/no-`Z` rows ('Z' is ASCII 90, above any digit), so a `…:00Z` row would
 * wrongly sort after a later `…:01` row.
 */
export function getLastModifiedMs(po: {
  updated_at?: string | null;
  created_at: string;
  flows?: ReadonlyArray<{ updated_at?: string | null }> | null;
}): number {
  return apiInstantMs(getLastModifiedAt(po));
}
